import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getContrastingTextColor } from '../utils/colorUtils';

const ColorContext = createContext();

// 🎨 cores padrão
const defaultColors = {
  primaryColor: '#007bff',
  secondaryColor: '#6c757d',

  // background da página (surface = cards, boxes)
  backgroundColor: '#f5f5f5', // fundo da página por padrão
  surfaceColor: '#ffffff',    // superfícies (cards)

  textColor: '#212529',
  textColorLight: '#ffffff',

  buttonColor: '#007bff',
  buttonTextColor: '#ffffff',

  cardBackgroundColor: '#ffffff',
  cardTextColor: '#212529',

  // Comportamento: se true, o body seguirá a primaryColor ao invés de backgroundColor
  followPrimary: false,

  darkMode: false,
};

export const ColorProvider = ({ children, initialColors }) => {
  // mescla initialColors (se houver) com defaults
  const mappedColors = initialColors ? {
    ...initialColors,
    backgroundColor: initialColors.BackgroundColor || initialColors.backgroundColor, // Mapeia o novo campo
  } : {};
  const initial = { ...defaultColors, ...mappedColors };
  const [colors, setColors] = useState(initial);

  // Aplica as variáveis CSS e atualiza document.body
  const applyColorsAsCssVariables = useCallback((c) => {
    const root = document.documentElement;

    // principais
    root.style.setProperty('--primary-color', c.primaryColor);
    root.style.setProperty('--secondary-color', c.secondaryColor);

    // fundo e superfícies
    root.style.setProperty('--background-color', c.backgroundColor);
    root.style.setProperty('--surface-color', c.surfaceColor);

    // textos
    root.style.setProperty('--text-color', c.textColor);
    root.style.setProperty('--text-color-light', c.textColorLight);

    // botões
    root.style.setProperty('--button-color', c.buttonColor);
    root.style.setProperty('--button-text-color', c.buttonTextColor);

    // cards
    root.style.setProperty('--card-background-color', c.cardBackgroundColor);
    root.style.setProperty('--card-text-color', c.cardTextColor);

    // contrastes calculados
    root.style.setProperty('--primary-text-contrast', getContrastingTextColor(c.primaryColor, c.textColor));
    root.style.setProperty('--background-text-contrast', getContrastingTextColor(c.backgroundColor, c.textColor));
    root.style.setProperty('--surface-text-contrast', getContrastingTextColor(c.surfaceColor, c.textColor));
    root.style.setProperty('--button-text-contrast', getContrastingTextColor(c.buttonColor, c.buttonTextColor));

    // Transição suave (opcional) — aplicado ao root para evitar flash abrupto
    root.style.setProperty('--theme-transition', 'background-color 200ms ease, color 200ms ease');

    // Atualiza o body diretamente para garantir que o index.html mude imediatamente.
    // Se followPrimary for true, usa primaryColor — caso contrário usa backgroundColor.
    try {
      const body = document.body;
 // Mantém a estrutura, mas agora sempre usa backgroundColor
if (c.followPrimary) {
  body.style.backgroundColor = c.backgroundColor;
  body.style.color = getContrastingTextColor(c.backgroundColor, c.textColor);
} else {
  body.style.backgroundColor = c.backgroundColor;
  body.style.color = c.textColor;
}



      // aplica transição suave no body (evita flash)
      body.style.transition = 'background-color 200ms ease, color 200ms ease';
    } catch (err) {
      // ambiente sem DOM (SSR) — ignora
      // console.warn('ColorContext: não foi possível setar body style (SSR?)', err);
    }
  }, []);

  // aplica ao montar e sempre que colors mudar
  useEffect(() => {
    applyColorsAsCssVariables(colors);
  }, [colors, applyColorsAsCssVariables]);

  // Atualiza parcial de cores — passa um objeto com apenas as propriedades que deseja mudar
  const updateColors = useCallback((newConfig = {}) => {
    setColors((prev) => {
      const merged = { ...prev, ...newConfig };

      // comportamento automático para darkMode (se passado explicitamente)
      if (newConfig.darkMode !== undefined) {
        if (newConfig.darkMode) {
          merged.backgroundColor = merged.backgroundColorDark || '#1f1f1f';
          merged.surfaceColor = merged.surfaceColorDark || '#2a2a2a';
          merged.textColor = merged.textColorLight || '#f5f5f5';
          merged.cardBackgroundColor = merged.cardBackgroundColorDark || '#2a2a2a';
          merged.cardTextColor = merged.cardTextColorDark || '#f5f5f5';
        } else {
          merged.backgroundColor = defaultColors.backgroundColor;
          merged.surfaceColor = defaultColors.surfaceColor;
          merged.textColor = defaultColors.textColor;
          merged.cardBackgroundColor = defaultColors.cardBackgroundColor;
          merged.cardTextColor = defaultColors.cardTextColor;
        }
      }

      return merged;
    });
  }, []);

  // expõe API
  return (
    <ColorContext.Provider value={{ colors, updateColors, defaultColors }}>
      {children}
    </ColorContext.Provider>
  );
};

export const useColors = () => useContext(ColorContext);

export default ColorContext;

/*
USO:

1) Envolver o App:
import { ColorProvider } from './contexts/ColorContext';
ReactDOM.render(
  <ColorProvider initialColors={{ primaryColor: '#ff5722', followPrimary: true }}>
    <App />
  </ColorProvider>,
  document.getElementById('root')
);

2) Atualizar cores em runtime:
const { updateColors } = useColors();
updateColors({ primaryColor: '#00b894' });

3) Fazer o fundo seguir a primary color:
updateColors({ followPrimary: true });

4) Fazer o fundo seguir a backgroundColor:
updateColors({ followPrimary: false });

5) CSS:
No seu index.css (ou global) use:
body {
  background-color: var(--background-color, #f5f5f5);
  color: var(--text-color, #212529);
  transition: background-color 200ms ease, color 200ms ease;
}

.card {
  background-color: var(--surface-color, #ffffff);
  color: var(--surface-text-contrast, #212529);
}
*/
